import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import fuzzysort from "fuzzysort";
import { ScaleLoader } from "react-spinners";
import classNames from "classnames";
import {
  MarketplaceNameRow,
  detailCoordinate,
  getPharmacyDetail,
  loadPickablePharmacies,
  selectionFromRow,
} from "../../services/pharmacyLookupService";
import {
  ConsultPharmacyPair,
  ConsultPharmacySelection,
} from "../../types/consultOrder.types";
import {
  PHARMACY_PICKER_DEBOUNCE_MS,
  PHARMACY_PICKER_LIMIT,
  PHARMACY_PICKER_MIN_QUERY,
} from "../../utils/consultOrder.constants";
import styles from "./PharmacyPicker.module.scss";

// ITAM-2887 (Q3, Q11, Q16): the per-draft pharmacy picker. An advisor has no
// pharmacy of their own, so each reservation and each RFZO request names the
// pharmacy it goes to. Type-ahead over GET /marketplace/names (click-and-collect
// rows only, one list per session), fuzzy-matched client side. Picking a
// pharmacy fetches its detail for `haveRFZO` and the coordinates the delivery
// pre-checks run against; the write is keyed on the pair,
// so a late response for a pharmacy the advisor already changed away from is
// dropped by the context.

export interface PharmacyPickerProps {
  value: ConsultPharmacySelection | null;
  /** Called with the new pick (the context clears the items on a CHANGE). */
  onChange: (pharmacy: ConsultPharmacySelection | null) => void;
  /** Detail enrichment for the pick, keyed on the pair. */
  onDetail: (
    pair: ConsultPharmacyPair,
    patch: Partial<ConsultPharmacySelection>,
  ) => void;
  /** True when the draft already holds items: a change asks first. */
  hasItems: boolean;
  error?: string;
  disabled?: boolean;
}

type LoadStatus = "idle" | "loading" | "loaded" | "error";

const fieldLabel = (row: MarketplaceNameRow): string =>
  row.branchMarketplaceName && row.branchMarketplaceName !== row.name
    ? `${row.name} - ${row.branchMarketplaceName}`
    : row.name;

const PharmacyPicker: React.FC<PharmacyPickerProps> = ({
  value,
  onChange,
  onDetail,
  hasItems,
  error,
  disabled = false,
}) => {
  const [rows, setRows] = useState<MarketplaceNameRow[]>([]);
  const [loadStatus, setLoadStatus] = useState<LoadStatus>("idle");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);
  // "Promeni apoteku" with items present: confirm before the items go.
  const [confirmingChange, setConfirmingChange] = useState(false);
  // Editing mode: no value yet, or the advisor confirmed a change.
  const [editing, setEditing] = useState(value == null);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Focus bookkeeping for the confirm row: "Promeni apoteku" unmounts while
  // the row is up, so focus is handed to the row and back explicitly.
  const changeButtonRef = useRef<HTMLButtonElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  // Bumped by the retry control after a failed list load.
  const [reloadToken, setReloadToken] = useState(0);

  const reactId = useId();
  const inputId = `pharmacy-picker-${reactId}-input`;
  const listboxId = `pharmacy-picker-${reactId}-listbox`;
  const errorId = `pharmacy-picker-${reactId}-error`;
  const optionId = (row: MarketplaceNameRow) =>
    `${listboxId}-opt-${row.idMarketplace}-${row.idBranchMarketplace}`;

  // The list is (re)requested every time editing starts; the service caches
  // the promise per session, so this is one network request per session and
  // a free retry after a failed load. The effect must not depend on its own
  // status: with loadStatus in the deps, setLoadStatus("loading") re-ran the
  // effect, the cleanup flagged the first run cancelled, the re-run bailed on
  // the non-idle status, and the resolved list was dropped (endless spinner).
  useEffect(() => {
    if (!editing) {
      return;
    }
    let cancelled = false;
    setLoadStatus((current) => (current === "loaded" ? current : "loading"));
    loadPickablePharmacies()
      .then((list) => {
        if (!cancelled) {
          setRows(list);
          setLoadStatus("loaded");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [editing, reloadToken]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedQuery(query),
      PHARMACY_PICKER_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [query]);

  const results = useMemo(() => {
    const needle = debouncedQuery.trim();
    if (needle.length < PHARMACY_PICKER_MIN_QUERY || loadStatus !== "loaded") {
      return [] as MarketplaceNameRow[];
    }
    return fuzzysort
      .go(needle, rows, {
        keys: ["name", "branchMarketplaceName", "address"],
        limit: PHARMACY_PICKER_LIMIT,
        threshold: -10000,
      })
      .map((result) => result.obj);
  }, [debouncedQuery, rows, loadStatus]);

  useEffect(() => {
    setCursor(-1);
  }, [results]);

  // When the value is set from outside (re-entry with a preserved draft),
  // leave editing mode.
  useEffect(() => {
    if (value != null) {
      setEditing(false);
      setConfirmingChange(false);
    }
  }, [value]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleOutsideMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideMouseDown);
    return () =>
      document.removeEventListener("mousedown", handleOutsideMouseDown);
  }, [open]);

  const pick = useCallback(
    (row: MarketplaceNameRow) => {
      const selection = selectionFromRow(row);
      onChange(selection);
      setOpen(false);
      setCursor(-1);
      setQuery("");
      setDebouncedQuery("");
      setEditing(false);
      const pair: ConsultPharmacyPair = {
        marketplaceId: selection.marketplaceId,
        branchMarketplaceId: selection.branchMarketplaceId,
      };
      // Enrichment. A failure leaves haveRFZO and the coordinates null
      // ("unknown") and marks the detail as failed: the RFZO form shows a
      // softer notice, the delivery section degrades to the backend's own
      // zone check, and both let the backend decide.
      getPharmacyDetail(pair.marketplaceId, pair.branchMarketplaceId)
        .then((detail) => {
          onDetail(pair, {
            haveRFZO:
              typeof detail.haveRFZO === "number" ? detail.haveRFZO : null,
            name: detail.name || selection.name,
            branchName: detail.meaningfulName ?? selection.branchName,
            address: detail.address ?? selection.address,
            latitude: detailCoordinate(detail.latitude),
            longitude: detailCoordinate(detail.longitude),
            detailStatus: "loaded",
          });
        })
        .catch(() => {
          onDetail(pair, { detailStatus: "error" });
        });
    },
    [onChange, onDetail],
  );

  const requestChange = () => {
    if (hasItems) {
      setConfirmingChange(true);
      setTimeout(() => confirmButtonRef.current?.focus(), 0);
      return;
    }
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const confirmChange = () => {
    setConfirmingChange(false);
    onChange(null);
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const showResultsList = open && results.length > 0;
  const trimmed = debouncedQuery.trim();
  const showStatusRow =
    open &&
    !showResultsList &&
    (loadStatus === "error" ||
      (loadStatus === "loaded" &&
        trimmed.length >= PHARMACY_PICKER_MIN_QUERY));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (showResultsList && cursor >= 0 && cursor < results.length) {
        pick(results[cursor]);
      }
      return;
    }
    if (!showResultsList) {
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => (c < results.length - 1 ? c + 1 : c));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => (c > 0 ? c - 1 : 0));
    }
  };

  const activeDescendant =
    showResultsList && cursor >= 0 && cursor < results.length
      ? optionId(results[cursor])
      : undefined;

  if (value != null && !editing) {
    return (
      <div
        className={styles.PharmacyPicker}
        data-consult-field="pharmacy"
        tabIndex={-1}
      >
        <div className={styles.selectedCard}>
          <div className={styles.selectedMain}>
            <span className={styles.selectedName}>{value.name}</span>
            {value.branchName && value.branchName !== value.name && (
              <span className={styles.selectedBranch}>{value.branchName}</span>
            )}
            {value.address && (
              <span className={styles.selectedAddress}>{value.address}</span>
            )}
          </div>
          {!disabled && !confirmingChange && (
            <button
              type="button"
              ref={changeButtonRef}
              className={styles.changeButton}
              onClick={requestChange}
            >
              Promeni apoteku
            </button>
          )}
        </div>
        {confirmingChange && (
          <div className={styles.confirmRow} role="alertdialog">
            <span className={styles.confirmText}>
              Promena apoteke uklanja izabrane artikle. Nastaviti?
            </span>
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.secondary}
                onClick={() => {
                  setConfirmingChange(false);
                  setTimeout(() => changeButtonRef.current?.focus(), 0);
                }}
              >
                Odustani
              </button>
              <button
                type="button"
                ref={confirmButtonRef}
                className={styles.primary}
                onClick={confirmChange}
              >
                Nastavi
              </button>
            </div>
          </div>
        )}
        {error && (
          <span id={errorId} className={styles.fieldError} role="alert">
            {error}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={styles.PharmacyPicker} ref={rootRef}>
      <label className={styles.label} htmlFor={inputId}>
        Apoteka
      </label>
      <div className={styles.inputWrapper}>
        <input
          id={inputId}
          ref={inputRef}
          className={styles.input}
          data-consult-field="pharmacy"
          type="text"
          name="pharmacy-search-box"
          autoComplete="off"
          maxLength={64}
          placeholder="Pretražite apoteku po nazivu ili adresi"
          role="combobox"
          aria-expanded={showResultsList}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeDescendant}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          disabled={disabled}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setOpen(true)}
        />
        {loadStatus === "loading" && (
          <span className={styles.spinner} aria-hidden="true">
            <ScaleLoader color="#00b2b1" height={14} width={3} />
          </span>
        )}

        {(showResultsList || showStatusRow) && (
          <>
            {showStatusRow && (
              <div className={styles.statusRow} role="status">
                {loadStatus === "error" ? (
                  <>
                    Lista apoteka nije dostupna.{" "}
                    <button
                      type="button"
                      className={styles.retryButton}
                      onClick={() => setReloadToken((t) => t + 1)}
                    >
                      Pokušaj ponovo
                    </button>
                  </>
                ) : (
                  `Nema apoteka koje odgovaraju pretrazi "${trimmed}".`
                )}
              </div>
            )}
            {showResultsList && (
              <ul className={styles.listbox} id={listboxId} role="listbox">
                {results.map((row, i) => (
                  <li
                    key={`${row.idMarketplace}-${row.idBranchMarketplace}`}
                    id={optionId(row)}
                    className={classNames(styles.option, {
                      [styles.optionActive]: i === cursor,
                    })}
                    role="option"
                    aria-selected={i === cursor}
                    onMouseEnter={() => setCursor(i)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pick(row);
                    }}
                  >
                    <span className={styles.optionName}>{fieldLabel(row)}</span>
                    {row.address && (
                      <span className={styles.optionAddress}>{row.address}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
      {value != null && editing && (
        <button
          type="button"
          className={styles.cancelEdit}
          onClick={() => setEditing(false)}
        >
          Zadrži izabranu apoteku
        </button>
      )}
      {error && (
        <span id={errorId} className={styles.fieldError} role="alert">
          {error}
        </span>
      )}
    </div>
  );
};

export default PharmacyPicker;
